import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button.js';
import { Icon } from '@/components/ui/icon.js';

const HOLD_DELAY_MS = 300;
const HOLD_REPEAT_MS = 150;

interface Props {
  label: string;
  busy?: boolean;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
  /**
   * Press-and-hold fires `onClick` repeatedly (after a short delay) until release —
   * Plinko's rapid-fire drop. Keyboard activation still fires single clicks.
   */
  repeatOnHold?: boolean;
}

export function BetButton({ label, busy, disabled, danger, onClick, repeatOnHold = false }: Props) {
  const timer = useRef<{ delay: number | null; interval: number | null }>({ delay: null, interval: null });
  const pointerFired = useRef(false);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  const stopRepeat = () => {
    if (timer.current.delay != null) window.clearTimeout(timer.current.delay);
    if (timer.current.interval != null) window.clearInterval(timer.current.interval);
    timer.current = { delay: null, interval: null };
  };

  useEffect(() => stopRepeat, []);
  useEffect(() => {
    if (!repeatOnHold || disabled) stopRepeat();
  }, [repeatOnHold, disabled]);

  const holdProps = repeatOnHold
    ? {
        onPointerDown: () => {
          pointerFired.current = true;
          onClickRef.current();
          stopRepeat();
          timer.current.delay = window.setTimeout(() => {
            timer.current.interval = window.setInterval(() => onClickRef.current(), HOLD_REPEAT_MS);
          }, HOLD_DELAY_MS);
        },
        onPointerUp: stopRepeat,
        onPointerLeave: stopRepeat,
        onPointerCancel: stopRepeat,
      }
    : {};

  return (
    <Button
      size="lg"
      glow
      fullWidth
      disabled={disabled}
      variant={danger ? 'danger' : undefined}
      onClick={() => {
        // pointerdown already fired this press; only keyboard activation reaches here
        if (pointerFired.current) {
          pointerFired.current = false;
          return;
        }
        onClick();
      }}
      {...holdProps}
    >
      <Icon name={danger ? 'close' : 'play'} size={18} />
      {busy ? `${label}…` : label}
    </Button>
  );
}
